import { merge } from "lodash";
import { defineStore } from "pinia";
import moment, { Moment } from "moment";

import { useStationStore } from "./station.store";

import { indicatorsConfiguration } from '~/configs/indicators/aura-indicators.config';

import { Measure } from "~/api/models/measure.model";
import { Station } from "~/api/models/station.model";
import { IndicatorConfiguration } from "~/configs/indicators/aura-indicators.model";

type Indicators = {[indicator: string]: {x: number, y: number}[]};
type StationMeasuresByIndicator = { [stationId: string]:  Indicators};

export const useAdvancedViewStore = defineStore('advancedView', {
    state: () => ({
        selected: [] as Station[] | null,
        measures: {} as StationMeasuresByIndicator,
        config: indicatorsConfiguration,
        loading: false,
        mode: 'live' as 'live' | 'hourly',
        since: moment().subtract(1, 'w'),
        before: moment()
    }),

    getters: {
        
        indicators(): IndicatorConfiguration[] {

            const indicators = new Set<string>();
            const measures = this.measures as Indicators[];
            Object.values(measures).forEach((measures: Indicators) => {
                Object.keys(measures).forEach((indicator) => indicators.add(indicator));
            });

            const availableIndicators = Array.from(indicators);

            return this.config
                .filter((config:IndicatorConfiguration) => !config.hide)
                .filter((config: IndicatorConfiguration) => availableIndicators.includes(config.key))
                .sort((c1,c2) => c1.priority - c2.priority)
        },
    },

    actions: {

        async selectStation(stationIds?: string[]): Promise<void> {
            const stations = useStationStore().allStations;
            if(stations && stations.length === 0) {
                return;
            }
            
            // select first station if no station is specified
            if((!stationIds || !stationIds.length)) {
                stationIds = [stations[0].id];
            }
            
            try {
                this.selected = stationIds
                    .map((id: string) => stations.find((station: Station) => station.id === id))
                    .filter(s => s);
            } catch(e) {
                this.selected = [];
                throw e;
            }
        },
        
        /**
         * Fetch all measures for each stations
         */
        async refresh(): Promise<StationMeasuresByIndicator> {
            this.loading = true;

            return Promise.all(
                this.selected.map((station: Station) => {
                    return this.$stationService
                        .getMeasures(station.id, this.since, this.before, this.mode==='hourly')
                        .then((measures: Measure[]) => ({ station, measures }))
                })
            ).then((results: { station: Station, measures: Measure[] }[]) => {
                const computed = {};
                results.forEach(({ station, measures }) => {
                    computed[station.id] = computed[station.id] || {};
                    measures.forEach((measure: Measure) => {
                        const allValues = merge(measure.values, measure.rawValues);
                        Object.keys(allValues).forEach((indicator: string) => {
                            computed[station.id][indicator] = computed[station.id][indicator] || [];
                            if(allValues[indicator] !== undefined) {
                                computed[station.id][indicator].push({x: new Date(measure.date * 1000), y: allValues[indicator]});
                            }
                        });
                    });
                });
                this.measures = computed;
                return computed;
            }).finally(() => {
                this.loading = false;
            });
        },

        setMode(mode: 'live' | 'hourly', refresh: boolean = true) {
            if(!(['live', 'hourly'].includes(mode))) {
                return;
            }
            this.mode = mode;

            if(refresh) {
                this.refresh();
            }
        },

        async setDatetimeRange(start: Moment, end: Moment, refresh: boolean = true) {
            this.since = start;
            this.before = end;
            
            if(refresh) {
                this.refresh();
            }
        },
    }
});